import { useEffect, useRef, useState } from "react";
import { getShopifyImageUrl } from "@/lib/shopify";

interface AdaptiveProductImageProps {
  url: string;
  alt: string;
  /** Card aspect ratio as width / height (e.g. 4/5 = 0.8) */
  cardAspect?: number;
  className?: string;
  priority?: boolean;
  hoverZoom?: boolean;
}

/**
 * Normalises wildly inconsistent store photography into one consistent frame:
 * measures the real image ratio, then picks cover (with focal-safe crop) for
 * shots close to the card ratio, and contain (with padding) for extreme ratios.
 */
export const AdaptiveProductImage = ({
  url,
  alt,
  cardAspect = 4 / 3,
  className = "",
  priority = false,
  hoverZoom = true,
}: AdaptiveProductImageProps) => {
  const ref = useRef<HTMLImageElement>(null);
  const [fit, setFit] = useState<"cover" | "contain">("cover");
  const [loaded, setLoaded] = useState(false);

  const measure = (img: HTMLImageElement) => {
    const ar = img.naturalWidth / img.naturalHeight;
    if (!ar || !isFinite(ar)) return;
    const ratio = ar / cardAspect;
    // Within 55% of the card ratio → crop to fill for a flush, editorial frame.
    setFit(ratio > 0.62 && ratio < 1.75 ? "cover" : "contain");
    setLoaded(true);
  };

  useEffect(() => {
    const img = ref.current;
    if (img?.complete && img.naturalWidth) measure(img);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return (
    <img
      ref={ref}
      src={getShopifyImageUrl(url, 900)}
      srcSet={`${getShopifyImageUrl(url, 600)} 600w, ${getShopifyImageUrl(url, 900)} 900w, ${getShopifyImageUrl(url, 1400)} 1400w`}
      sizes="(max-width: 768px) 70vw, 30vw"
      alt={alt}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      onLoad={(e) => measure(e.currentTarget)}
      className={`w-full h-full transition-all duration-700 ease-out ${
        fit === "cover" ? "object-cover" : "object-contain"
      } ${loaded ? "opacity-100" : "opacity-0"} ${
        hoverZoom ? "group-hover:scale-[1.04]" : ""
      } ${className}`}
      style={{ objectPosition: "center center" }}
    />
  );
};
